import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@components-library/Icons/SvgIcon';
import { CountryInfo } from '@components-shared/countryDetection/countryDetector';
import { Modal } from '@components-shared/modal';
import { keyMap } from '@locales/lokalise/keyMap';
import { trackEventWithDataInTrackingSystem } from '@utils/errors';
import { useBrokerParams } from '@utils/hooks/useBrokerParams';
import { browserService } from '@utils/library';
import { getCurrentSessionUserId } from '@utils/userInfoStore';
import AlertBlocked from 'assets/icons/alert-blocked.withAttrs.svg';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import styles from './styles.module.scss';

export const GuardCountrySupport = observer((props: PropsWithChildren<unknown>) => {
  const { i18n } = useTranslation();
  const [isModalShown, setIsModalShown] = useState<boolean>(false);
  const [userCountry, setUserCountry] = useState<CountryInfo | null>(null);
  const {
    accountStore: { userCountryInfo },
  } = useMobxStore();

  const brokerParams = useBrokerParams();

  const brokerName = brokerParams.currentLanguage?.brokerName ?? brokerParams.defaultLanguage?.brokerName;

  useEffect(() => {
    const forbiddenCountryCodes = window.settings?.country
      .filter((country) => country.restricted)
      .map((country) => country.code);

    const getCountry = async () => {
      const detectedCountry = userCountryInfo;

      if (detectedCountry && forbiddenCountryCodes?.includes(detectedCountry.countryCode)) {
        setIsModalShown(true);
        setUserCountry(detectedCountry);

        trackEventWithDataInTrackingSystem('The country is not supported', {
          detectedCountry,
          sessionId: getCurrentSessionUserId(),
          browserId: browserService.browserUniqueId,
        });
      }
    };

    void getCountry();
  }, []);

  const handleModalClose = () => {
    setIsModalShown(false);
  };

  const countryName = userCountry?.countryName ?? '';

  return (
    <>
      {isModalShown && (
        <Modal closeOnEscKey closeHandler={handleModalClose}>
          <div className={styles.warningContainer}>
            <SvgIcon src={AlertBlocked} className={styles.warningSvgImage} />
            <h2 className={styles.title}>{i18n.t(keyMap.countrySupportGuardTitle)}</h2>
            <hr className={styles.divider} />
            <p>{i18n.t(keyMap.countrySupportGuardSubTitleOne, { countryName })}</p>
            <p>
              {i18n.t(keyMap.countrySupportGuardSubTitleTwo, {
                countryName,
                brokerName: brokerName ?? '',
              })}
            </p>
            <p>{i18n.t(keyMap.countrySupportGuardSubTitleThree, { countryName })}</p>

            <button type="button" className="form__button" onClick={handleModalClose}>
              {i18n.t(keyMap.countrySupportGuardContinueBrowsing)}
            </button>
          </div>
        </Modal>
      )}
      {props.children}
    </>
  );
});
